/**
 * Scoring resume helpers — persist the current hole per (trip, round, group)
 * so that re-entry from leaderboard / browser-back / "Continue Scoring"
 * lands on the right hole instead of hole 1.
 */

function _key(tripCode: string, roundId: string, groupId: string): string {
  return `ft_resume:${tripCode.toUpperCase()}:${roundId}:${groupId}`;
}

/** Write the current hole index (0-17) to localStorage. */
export function writeScoringHole(tripCode: string, roundId: string, groupId: string, hole: number): void {
  try { localStorage.setItem(_key(tripCode, roundId, groupId), String(hole)); } catch {}
}

/** Read the stored hole index, or null if absent / invalid. */
export function readScoringHole(tripCode: string, roundId: string, groupId: string): number | null {
  try {
    const v = localStorage.getItem(_key(tripCode, roundId, groupId));
    if (v == null) return null;
    const n = parseInt(v, 10);
    return n >= 0 && n <= 17 ? n : null;
  } catch { return null; }
}

/** Clear the stored hole (e.g. on round completion). */
export function clearScoringHole(tripCode: string, roundId: string, groupId: string): void {
  try { localStorage.removeItem(_key(tripCode, roundId, groupId)); } catch {}
}

/**
 * Determine which hole to show when the scoring screen mounts or a group is selected.
 *
 * Priority:
 *  1. Stored lastHoleIndex from localStorage → return it (resume where they left off).
 *  2. No stored hole → first unscored hole in the group's playingOrder (shotgun/two-tee aware).
 *  3. Fallback → group's starting hole (first in playingOrder), not blindly hole 1.
 *
 * @param holeStrokes  18-length array; 0 = unscored.
 * @param playingOrder 18-length array from playingOrderForGroup (0-indexed scorecard holes
 *                     in the order the group plays them). If omitted, defaults to [0..17].
 */
export function resumeHole(
  tripCode: string,
  roundId: string,
  groupId: string,
  holeStrokes: number[],
  playingOrder?: number[],
): number {
  const order = playingOrder && playingOrder.length === 18
    ? playingOrder
    : Array.from({ length: 18 }, (_, i) => i);

  // 1. Stored hole → resume where they left off
  const stored = readScoringHole(tripCode, roundId, groupId);
  if (stored !== null) return stored;

  // 2. First unscored hole in playing order
  const firstUnscored = order.find(h => (holeStrokes[h] || 0) === 0);
  if (firstUnscored !== undefined) return firstUnscored;

  // 3. All scored → group's starting hole (first in playing order)
  return order[0];
}

/**
 * Build a 18-length aggregate strokes array for a group in individual scoring.
 * A hole is "unscored" (0) if ANY player in the group has 0 strokes on it.
 */
export function groupAggregateStrokes(round: any, groupIdx: number): number[] {
  const teams: string[][] = round?.teams || [];
  const pids = teams[groupIdx] || [];
  const hs: Record<string, number[]> = round?.hole_strokes || {};
  const result = new Array(18).fill(1); // default: scored
  for (let h = 0; h < 18; h++) {
    for (const pid of pids) {
      if (((hs[pid] || [])[h] || 0) === 0) {
        result[h] = 0;
        break;
      }
    }
  }
  return result;
}
